// Find file: text that names a file (a path, a filename, `path:42`) found
// among the repository's files at the pull request's head. Pure: the view
// reads the selection and the tree, and navigates.
import type { LineSpan } from './snapshotLink';

/** A selection read as a file reference: the path or name to look for, and the lines it names. */
export interface FileRef {
  query: string;
  line: LineSpan | null;
}

/** How a file matched: the whole path, a trailing run of directories, its name, or its letters in order. */
export type MatchKind = 'exact' | 'suffix' | 'basename' | 'fuzzy';

export interface FileMatch {
  path: string;
  kind: MatchKind;
  /** The pull request changes this file. */
  changed: boolean;
}

/** Longest selection read as a path, in UTF-16 code units. */
export const MAX_REF = 300;

/** Most matches listed. */
export const MAX_MATCHES = 50;

const EXTENSIONS = new Set(
  (
    'md markdown mdx txt rst adoc org rs ts tsx mts cts js jsx mjs cjs svelte vue astro json jsonc json5 yaml yml toml lock ini cfg conf ' +
    'env sh bash zsh fish ps1 bat py pyi rb go mod sum java kt kts scala swift m mm c h cc cpp cxx hpp hh cs fs ex exs erl hrl hs elm ml mli ' +
    'clj cljs lua php pl pm r jl sql graphql gql proto thrift css scss sass less html htm xml xsd svg png jpg jpeg gif webp avif ico pdf ' +
    'csv tsv wasm wat tf tfvars hcl nix gradle properties qual patch diff log ipynb zig dart sol vy cmake mk bzl bazel snap tmpl j2 ' +
    'dockerignore gitignore gitattributes editorconfig'
  ).split(' '),
);

const BASENAMES = new Set(
  (
    'Makefile makefile GNUmakefile Dockerfile Containerfile Justfile justfile Rakefile Gemfile Procfile Vagrantfile Jenkinsfile Brewfile ' +
    'Pipfile Tiltfile Caddyfile LICENSE LICENCE COPYING README CHANGELOG CHANGES CONTRIBUTING AUTHORS NOTICE CODEOWNERS OWNERS ' +
    'SECURITY BUILD WORKSPACE'
  ).split(' '),
);

const QUOTES = `"'\`<>`;
const TRAILING = `"'\`>.,;:!?`;
const PAIRS: [string, string][] = [
  ['(', ')'],
  ['[', ']'],
  ['{', '}'],
];

function count(s: string, ch: string): number {
  let n = 0;
  for (const c of s) if (c === ch) n++;
  return n;
}

/** Whether the bracket opening `s` is the one closing it (`(a)`, not `(a)/(b)`). */
function wraps(s: string, open: string, close: string): boolean {
  if (s.length < 2 || s[0] !== open || s[s.length - 1] !== close) return false;
  let depth = 0;
  for (let i = 0; i < s.length; i++) {
    if (s[i] === open) depth++;
    else if (s[i] === close && --depth === 0) return i === s.length - 1;
  }
  return false;
}

/**
 * `text` without wrapping quotes and trailing punctuation, and without
 * brackets that wrap all of it or that have no partner: `(README.md).`
 * and `(see` lose theirs, `(app)/+page.svelte` and `[id]/x.ts` keep them.
 */
function unwrap(text: string): string {
  let s = text.trim();
  for (;;) {
    const before = s;
    while (s && QUOTES.includes(s[0])) s = s.slice(1);
    while (s && TRAILING.includes(s[s.length - 1])) s = s.slice(0, -1);
    for (const [open, close] of PAIRS) {
      if (wraps(s, open, close)) s = s.slice(1, -1);
      else if (s[0] === open && count(s, open) > count(s, close)) s = s.slice(1);
      else if (s[s.length - 1] === close && count(s, close) > count(s, open)) s = s.slice(0, -1);
    }
    if (s === before) return s;
  }
}

/**
 * `text` split into a path and the lines its suffix names: `:42`,
 * `:42:7` (a column, dropped), `:40-44`, `#L42`, `#L40-L44` or `#L40-44`.
 * Text without a suffix (or with line 0) is all path.
 */
export function splitLineSuffix(text: string): { path: string; line: LineSpan | null } {
  const m = /^(.+?)(?::(\d+)(?:-(\d+)|:\d+)?|#L(\d+)(?:-L?(\d+))?)$/.exec(text);
  if (!m) return { path: text, line: null };
  const a = Number(m[2] ?? m[4]);
  const end = m[3] ?? m[5];
  const b = end === undefined ? a : Number(end);
  if (!(a >= 1 && b >= 1)) return { path: text, line: null };
  return { path: m[1], line: { start: Math.min(a, b), end: Math.max(a, b) } };
}

const URLISH = /^(?:[a-z][a-z0-9+.-]*:\/\/|www\.|(?:mailto|tel|data|javascript|file):)/i;
const EMAIL = /^[^/@\s]+@[^/@\s]+\.[a-z]{2,}$/i;
const VERSION = /^v?\d+(?:\.\d+)+(?:[-+.][0-9A-Za-z.-]+)?$/;
/** Characters of a path: letters and digits of any script, and the punctuation file names carry (`+page`, `[slug]`, `(group)`, `$lib`, `100%`). */
const PATH_CHARS = /^[\p{L}\p{N}\p{M}_@+~.\-/[\]()$%=,]+$/u;
/** A segment with nothing but brackets, `$` and punctuation names no folder or file. */
const SEGMENT = /[\p{L}\p{N}]/u;
/** A bracketed part of a segment (`[a,b]`, `(a,b)`), the only place a comma belongs to a path. */
const BRACKETED = /\[[^[\]/]*\]|\([^()/]*\)/g;

/** Whether `path` has a comma outside brackets, which separates names (`a.ts,b.ts`). */
function listsNames(path: string): boolean {
  let s = path;
  for (let prev = ''; s !== prev; ) {
    prev = s;
    s = s.replace(BRACKETED, '');
  }
  return s.includes(',');
}

function extensionOf(name: string): string | null {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : null;
}

/** A file name by its look alone: a known extension, a known name, or a dotfile. */
function looksLikeName(name: string): boolean {
  if (BASENAMES.has(name)) return true;
  if (/^\.[A-Za-z][\w.-]*$/.test(name)) return true;
  const ext = extensionOf(name);
  return ext !== null && EXTENSIONS.has(ext);
}

/**
 * The file a selection names, or null for prose, URLs, email addresses,
 * version numbers and anything over MAX_REF. A selection names a file when,
 * once its quotes, brackets and trailing punctuation are dropped, it is one
 * word of path characters that has a known extension, is a known file name
 * (`Makefile`, `README`) or a dotfile, starts with `./` or `../`, or has
 * three or more segments (`src/lib/router`). A comma counts as a path
 * character only inside brackets (`[a,b]`). A leading `./` or `/` is
 * dropped from the query.
 */
export function detectPath(text: string): FileRef | null {
  if (text.length > MAX_REF) return null;
  const s = unwrap(text);
  if (!s || /\s/.test(s) || URLISH.test(s) || EMAIL.test(s)) return null;
  const { path: raw, line } = splitLineSuffix(s);
  const path = unwrap(raw).replace(/^\.\//, '').replace(/^\/(?!\/)/, '');
  if (!path || !PATH_CHARS.test(path) || path.includes('//') || VERSION.test(path) || listsNames(path)) return null;
  const segments = path.split('/');
  const name = segments[segments.length - 1];
  if (!name || /^\.+$/.test(name)) return null;
  const relative = raw.startsWith('./') || raw.startsWith('../');
  // `..`, and the `@` and `~` of an import alias, are the only segments without a letter or digit.
  if (!segments.every((seg, i) => SEGMENT.test(seg) || seg === '..' || (i === 0 && (seg === '@' || seg === '~')))) return null;
  // Dates (`2026/10/06`) are numbers between slashes.
  if (segments.every((seg) => /^\d+$/.test(seg))) return null;
  if (!(looksLikeName(name) || relative || segments.length >= 3)) return null;
  return { query: path, line };
}

/**
 * The query as the part a repository path can end with: without leading
 * `./`, `../` and slashes, with an import alias's root dropped (`@/x`,
 * `~/x`: `x`) or read as a folder (`$lib/x`: `lib/x`, which SvelteKit's
 * `$lib` is, under `src/`).
 */
function normalize(query: string): string {
  return query
    .replace(/^(?:\.\.?\/|\/)+/, '')
    .replace(/^[@~]\//, '')
    .replace(/^\$([\w-]+)\//, '$1/');
}

function withoutExtension(path: string): string {
  const slash = path.lastIndexOf('/');
  const dot = path.lastIndexOf('.');
  return dot > slash + 1 ? path.slice(0, dot) : path;
}

function basename(p: string): string {
  return p.slice(p.lastIndexOf('/') + 1);
}

function stem(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot > 0 ? name.slice(0, dot) : name;
}

/** The length of the shortest window of `path` holding `q`'s letters in order (case folded), or null. */
function fuzzySpan(q: string, path: string): number | null {
  const p = path.toLowerCase();
  let best: number | null = null;
  for (let start = p.indexOf(q[0]); start >= 0; start = p.indexOf(q[0], start + 1)) {
    let i = 1;
    let j = start + 1;
    while (i < q.length && j < p.length) {
      if (p[j] === q[i]) i++;
      j++;
    }
    if (i < q.length) break;
    const span = j - start;
    if (best === null || span < best) best = span;
  }
  return best;
}

const RANK: Record<MatchKind, number> = { exact: 0, suffix: 1, basename: 2, fuzzy: 3 };

/**
 * The files `query` may name, best first: the exact path, then paths that
 * end with it at a directory boundary, then files of the same name (case
 * folded, or the same name without its extension for an import such as
 * `./router`). A path that ends with the query but for its extension
 * (`$lib/api/client`) is a suffix match too. Only when none of these match,
 * files holding the query's letters in order, tightest first. Within a
 * kind, files in more of the folders the query names come first, then files
 * the pull request changes, then shorter paths. At most `limit`.
 */
export function rankMatches(query: string, paths: Iterable<string>, changed: Set<string>, limit = MAX_MATCHES): FileMatch[] {
  const q = normalize(query);
  if (!q) return [];
  const qName = basename(q);
  const qLower = qName.toLowerCase();
  const qDirs = q.split('/').slice(0, -1);
  type Ranked = FileMatch & { score: number; common: number };
  const named: Ranked[] = [];
  const fuzzy: Ranked[] = [];
  const fq = q.toLowerCase().replace(/\//g, '');
  for (const path of paths) {
    const isChanged = changed.has(path);
    const name = basename(path);
    let kind: MatchKind | null = null;
    let score = 0;
    const bare = withoutExtension(path);
    if (path === q) kind = 'exact';
    else if (path.endsWith('/' + q)) kind = 'suffix';
    else if (bare === q || bare.endsWith('/' + q)) {
      // An import names a module without its extension (`$lib/api/client`).
      kind = 'suffix';
      score = 1;
    } else if (name === qName) kind = 'basename';
    else if (name.toLowerCase() === qLower || stem(name) === qName) {
      kind = 'basename';
      score = 1;
    }
    if (kind) {
      const dirs = path.split('/').slice(0, -1);
      named.push({ path, kind, changed: isChanged, score, common: qDirs.filter((d) => dirs.includes(d)).length });
      continue;
    }
    if (named.length || !fq) continue;
    const span = fuzzySpan(fq, path.replace(/\//g, ''));
    if (span !== null) fuzzy.push({ path, kind: 'fuzzy', changed: isChanged, score: span, common: 0 });
  }
  const list = named.length ? named : fuzzy;
  list.sort(
    (a, b) =>
      RANK[a.kind] - RANK[b.kind] ||
      a.score - b.score ||
      b.common - a.common ||
      Number(b.changed) - Number(a.changed) ||
      a.path.length - b.path.length ||
      (a.path < b.path ? -1 : a.path > b.path ? 1 : 0),
  );
  return list.slice(0, limit).map(({ path, kind, changed }) => ({ path, kind, changed }));
}

/**
 * Whether a selection is worth offering: the repository has a file by its
 * name (not only a loose match), or, in a tree GitHub lists only in part
 * (`lazy`), it is a path, which may be in a folder not listed yet.
 */
export function offerable(ref: FileRef, matches: FileMatch[], lazy: boolean): boolean {
  return matches.some((m) => m.kind !== 'fuzzy') || (lazy && ref.query.includes('/'));
}

/**
 * The folders a query names, outermost first (`src/lib/router.ts`: `src`,
 * `src/lib`): what a tree GitHub lists only in part loads before matching.
 */
export function queryDirs(query: string): string[] {
  const parts = normalize(query).split('/').slice(0, -1);
  return parts.map((_, i) => parts.slice(0, i + 1).join('/'));
}

/** The query parameters naming the comparison the Files view shows, which a found file opens in. */
const COMPARISON = ['from', 'to', 'ws'];

/**
 * The Files view at `filesPath` (a pull request's, a compare's) on `path`,
 * scrolled to `line` when given, in the comparison `current` names (its
 * `from`, `to` and `ws`); the path's own query (a view) is kept.
 */
export function findHref(filesPath: string, path: string, line: LineSpan | null, current?: URLSearchParams): string {
  const url = new URL(filesPath, 'http://app');
  url.searchParams.set('file', path);
  for (const k of COMPARISON) {
    const v = current?.get(k);
    if (v) url.searchParams.set(k, v);
  }
  if (line) url.searchParams.set('line', line.start === line.end ? String(line.start) : `${line.start}-${line.end}`);
  return url.pathname + url.search;
}

/** GitHub code search for files of `owner/repo` whose path contains `query`. */
export function codeSearchUrl(webUrl: string, owner: string, repo: string, query: string): string {
  const q = new URLSearchParams({ type: 'code', q: `repo:${owner}/${repo} path:${normalize(query)}` });
  return `${webUrl.replace(/\/+$/, '')}/search?${q.toString()}`;
}
